import { Link } from "react-router-dom";

const products = [
  {
    id: "intake-agent",
    gradient: "from-blue-600 to-blue-800",
    icon: "ri-inbox-2-line",
    title: "AI Intake Agent",
    price: "€490",
    tagline: '"Handle every incoming inquiry automatically"',
    description:
      "Never miss a lead or client request again. The AI Intake Agent captures, qualifies, and routes every incoming inquiry — email, web form, or message — 24/7, without a human touching it.",
    features: [
      "Instant response to all incoming inquiries",
      "Automatic lead qualification and scoring",
      "Smart routing to the right team member",
      "CRM sync and follow-up scheduling",
      "Multi-channel: email, web, WhatsApp",
    ],
    href: "/products#intake-agent",
  },
  {
    id: "appointment-maker",
    gradient: "from-cyan-600 to-blue-700",
    icon: "ri-calendar-check-line",
    title: "AI Appointment Maker",
    price: "€390",
    tagline: '"Your calendar fills itself"',
    description:
      "Say goodbye to the back-and-forth of scheduling. The AI Appointment Maker reads your availability, communicates with clients, and books confirmed meetings — completely on autopilot.",
    features: [
      "Automated scheduling from first contact",
      "Real-time calendar sync (Google, Outlook)",
      "Automatic reminders and confirmations",
      "Rescheduling handled without staff",
      "No-show reduction up to 40%",
    ],
    href: "/products#appointment-maker",
  },
  {
    id: "document-intelligence",
    gradient: "from-violet-600 to-blue-700",
    icon: "ri-file-text-line",
    title: "AI Document Intelligence",
    price: "€590",
    tagline: '"Stop reading documents, start understanding them"',
    description:
      "From contracts to reports, the AI Document Intelligence module reads, extracts, summarises and flags what matters. What used to take hours of staff time happens in seconds.",
    features: [
      "Automatic document classification",
      "Key clause and data extraction",
      "Risk and anomaly flagging",
      "Plain-language summaries",
      "Works with PDF, Word, scanned docs",
    ],
    href: "/products#document-intelligence",
  },
  {
    id: "knowledge-assistant",
    gradient: "from-emerald-600 to-blue-700",
    icon: "ri-robot-line",
    title: "AI Knowledge Assistant",
    price: "€490",
    tagline: '"The answer to every question your team asks every day"',
    description:
      "Your entire company's knowledge — procedures, policies, product info, FAQs — made instantly accessible. Staff stop interrupting each other and start getting answers in seconds.",
    features: [
      "Trained on your own documents and data",
      "Instant answers in plain language",
      "Available 24/7 to staff and clients",
      "Learns and improves over time",
      "Reduces onboarding time by 60%",
    ],
    href: "/products#knowledge-assistant",
  },
];

export default function ProductsSection() {
  return (
    <section className="py-24 bg-[#070C18]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="text-electric-400 text-xs font-semibold uppercase tracking-widest">Modular Solutions</span>
          <h2 className="mt-3 text-4xl lg:text-5xl font-extrabold text-white leading-tight">
            Start With One.{" "}
            <span className="text-electric-400">Scale As You Grow.</span>
          </h2>
          <p className="mt-4 text-slate-400 text-lg">No IT project. No coding. Results in days.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              id={product.id}
              className="group flex flex-col bg-[#0D1628] border border-white/8 rounded-2xl overflow-hidden hover:border-electric-500/30 transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`bg-gradient-to-br ${product.gradient} p-6 flex items-center gap-4`}>
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-white/15 shrink-0">
                  <i className={`${product.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <p className="text-white font-bold text-lg leading-tight">{product.title}</p>
                  <p className="text-white/70 text-sm font-semibold mt-0.5">
                    {product.price}
                    <span className="font-normal text-white/50">/mo</span>
                  </p>
                </div>
              </div>

              <div className="flex flex-col flex-1 p-5 gap-4">
                <p className="text-slate-300 text-sm italic font-medium">{product.tagline}</p>
                <p className="text-slate-400 text-sm leading-relaxed flex-1">{product.description}</p>
                <ul className="flex flex-col gap-2">
                  {product.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-slate-300">
                      <i className="ri-check-line text-electric-400 mt-0.5 shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  className="inline-flex items-center gap-1.5 text-electric-400 text-sm font-semibold hover:gap-2.5 transition-all duration-200 mt-auto cursor-pointer whitespace-nowrap"
                  to={product.href}
                >
                  Learn more <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-white font-medium text-sm hover:bg-white/10 hover:border-electric-500/30 transition-all duration-200 cursor-pointer whitespace-nowrap"
            to="/products"
          >
            View All Products & Bundles
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
